import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

import { SectionCard } from '../components/ttq/SectionCard';
import { useAuth } from '../context/AuthContext';
import { isApprovedFactoryManager } from '../lib/factoryContext';
import {
  factoryStaffService,
  type FactoryStaffCapability,
} from '../services/factoryStaffService';

const statusLabel: Record<string, string> = {
  pending: '待审批',
  approved: '已批准',
  suspended: '已停用',
  revoked: '已终止',
};

const roleLabel: Record<string, string> = {
  worker: '厂员',
  manager: '工厂管理员',
};

export function FactoryStaffAdminPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<FactoryStaffCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const list = await factoryStaffService.listCapabilities();
    setRows(list);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isApprovedFactoryManager(user)) {
        setLoading(false);
        return;
      }
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, user]);

  async function review(
    row: FactoryStaffCapability,
    status: 'approved' | 'suspended' | 'revoked',
    role?: 'worker' | 'manager',
  ) {
    setBusyId(row.userId);
    setError(null);
    try {
      await factoryStaffService.review(row.userId, { status, role });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  if (!isApprovedFactoryManager(user)) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        <h1 className="text-xl font-semibold text-ink">厂员管理</h1>
        <p className="text-sm text-muted">需要工厂管理员能力才能查看与审批厂员。</p>
        <Link to="/factory/settings" className="text-sm text-primary">
          返回设置
        </Link>
      </div>
    );
  }

  const pending = rows.filter((r) => r.status === 'pending');
  const others = rows.filter((r) => r.status !== 'pending');

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <Shield size={18} />
            <span className="text-sm font-semibold">工厂管理员</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-ink">厂员审批与身份管理</h1>
          <p className="mt-1 text-sm text-muted">
            审批其他人员的工厂员工申请，并可停用或终止其厂员身份。可查看全部厂员信息。
          </p>
        </div>
        <Link to="/factory/settings" className="text-sm text-primary">
          返回设置
        </Link>
      </div>

      {error ? (
        <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <SectionCard title="待审批申请" subtitle={`${pending.length} 人`}>
        {loading ? <p className="text-sm text-muted">加载中…</p> : null}
        {!loading && pending.length === 0 ? (
          <p className="text-sm text-muted">暂无待审批申请</p>
        ) : null}
        <ul className="space-y-3">
          {pending.map((row) => (
            <StaffRow
              key={row.id}
              row={row}
              busy={busyId === row.userId}
              onApprove={() => void review(row, 'approved', 'worker')}
              onApproveManager={() => void review(row, 'approved', 'manager')}
              onReject={() => void review(row, 'revoked')}
            />
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="全部厂员" subtitle={`${others.length} 人`}>
        {!loading && others.length === 0 ? (
          <p className="text-sm text-muted">暂无其他记录</p>
        ) : null}
        <ul className="space-y-3">
          {others.map((row) => (
            <StaffRow
              key={row.id}
              row={row}
              busy={busyId === row.userId}
              selfId={user?.id}
              onApprove={
                row.status !== 'approved'
                  ? () => void review(row, 'approved', row.role === 'manager' ? 'manager' : 'worker')
                  : undefined
              }
              onSuspend={
                row.status === 'approved' ? () => void review(row, 'suspended') : undefined
              }
              onRevoke={
                row.status === 'approved' || row.status === 'suspended'
                  ? () => void review(row, 'revoked')
                  : undefined
              }
            />
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

function StaffRow({
  row,
  busy,
  selfId,
  onApprove,
  onApproveManager,
  onReject,
  onSuspend,
  onRevoke,
}: {
  row: FactoryStaffCapability;
  busy: boolean;
  selfId?: string;
  onApprove?: () => void;
  onApproveManager?: () => void;
  onReject?: () => void;
  onSuspend?: () => void;
  onRevoke?: () => void;
}) {
  const isSelf = selfId && row.userId === selfId;
  return (
    <li className="rounded-xl border border-border bg-canvas/40 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">
            {row.username?.trim() || '未命名用户'}
            {isSelf ? <span className="ml-2 text-xs text-muted">（我）</span> : null}
          </div>
          <div className="mt-1 space-y-0.5 text-xs text-muted">
            <div>手机：{row.phone || '—'}</div>
            <div>
              {statusLabel[row.status] || row.status} · {roleLabel[row.role] || row.role}
            </div>
            <div>申请：{new Date(row.appliedAt).toLocaleString()}</div>
            {row.reviewedAt ? <div>审核：{new Date(row.reviewedAt).toLocaleString()}</div> : null}
            {row.note ? <div>备注：{row.note}</div> : null}
            <div className="font-mono text-[10px] opacity-70">user: {row.userId}</div>
            <div className="font-mono text-[10px] opacity-70">account: {row.accountId}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {onApprove ? (
            <button
              type="button"
              disabled={busy}
              onClick={onApprove}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs text-white disabled:opacity-50"
            >
              批准为厂员
            </button>
          ) : null}
          {onApproveManager ? (
            <button
              type="button"
              disabled={busy}
              onClick={onApproveManager}
              className="rounded-lg border border-border px-3 py-1.5 text-xs text-ink disabled:opacity-50"
            >
              批准为管理员
            </button>
          ) : null}
          {onReject ? (
            <button
              type="button"
              disabled={busy}
              onClick={onReject}
              className="rounded-lg border border-danger/40 px-3 py-1.5 text-xs text-danger disabled:opacity-50"
            >
              拒绝
            </button>
          ) : null}
          {onSuspend && !isSelf ? (
            <button
              type="button"
              disabled={busy}
              onClick={onSuspend}
              className="rounded-lg border border-border px-3 py-1.5 text-xs text-ink disabled:opacity-50"
            >
              停用
            </button>
          ) : null}
          {onRevoke && !isSelf ? (
            <button
              type="button"
              disabled={busy}
              onClick={onRevoke}
              className="rounded-lg border border-danger/40 px-3 py-1.5 text-xs text-danger disabled:opacity-50"
            >
              终止身份
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
